import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { CreateEvent, EventType } from '../types/Event';
import { getCalculateEventCost } from '../services/billingService';
import { useToast } from '../context/ToastContext';
import { UserRoleContext } from '../context/UserRoleContext';

interface EventDetailsPageProps {
  eventDetails: CreateEvent;
  eventType: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleEventTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  availableEventTypes: EventType[]; // Event types list passed from parent
  onValidityChange: (isValid: boolean) => void;
  TandC: boolean,
  setTandC: Dispatch<SetStateAction<boolean>>;
  setEventDetails: Dispatch<SetStateAction<CreateEvent>>;
}


const formatDateTime = (date: Date | string) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Extract local components
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  // Format as "YYYY-MM-DDTHH:mm"
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};



export const EventDetailsPage: React.FC<EventDetailsPageProps> = ({
  eventDetails,
  eventType,
  handleInputChange,
  handleEventTypeChange,
  availableEventTypes,
  onValidityChange,
  TandC,
  setTandC,
  setEventDetails
}) => {

  // const [TandC, setTandC] = useState<boolean>(false);
  const [isValid, setIsValid] = useState(false);

  const today = new Date();
  const minStartDate = today.toISOString().slice(0, 16);

  const maxStartDate = new Date(today);
  maxStartDate.setFullYear(today.getFullYear() + 1);
  const maxStartDateStr = maxStartDate.toISOString().slice(0, 16);
  const startDate = eventDetails.start_date ? new Date(eventDetails.start_date) : today;
  const minEndDate = startDate.toISOString().slice(0, 16);
  const maxEndDate = new Date(startDate);
  maxEndDate.setDate(startDate.getDate() + 30);
  const maxEndDateStr = maxEndDate.toISOString().slice(0, 16);
  const [dateError, setDateError] = useState<string>('');
  const { addToast } = useToast();
  const { userRole } = useContext(UserRoleContext);



useEffect(() => {
  const fetchCost = async () => {
    try {
      if(!eventDetails.expected_registrant_count) {
        return;
      }
      const response = await getCalculateEventCost(
        Number(eventDetails.expected_registrant_count),
        eventDetails.has_media ?? false
      );
      // Be sure to update state rather than mutate `eventDetails` directly
      setEventDetails(prev => ({
        ...prev,
        expected_cost: response
      }));
    } catch (error) {
      addToast('Error calculating price. Please check CyneUp.com for pricing');
    }
  };

  fetchCost();
}, [eventDetails.has_media, eventDetails.expected_registrant_count]);

  useEffect(() => {
    const isEventNameValid = eventDetails.event_name !== '';
    const isEventDescriptionValid = eventDetails.event_description !== '';
    const isStartDateValid = !!eventDetails.start_date;
    const isEndDateValid = !!eventDetails.end_date;
    const isEventTypeValid = eventType !== '';
    // const isRegistrantLimitValid = eventDetails.organization_registrant_limit >= 1 && eventDetails.organization_registrant_limit <= 100000;
    // const isBillingValid = eventDetails.use_billing_service !== undefined;
    // const isInvoiceValid = eventDetails.use_invoice !== undefined;
    const areTermsAccepted = TandC;


    const isFormValid = validateDates(eventDetails.start_date, eventDetails.end_date) && isEventNameValid && isEventDescriptionValid && isStartDateValid && isEndDateValid && isEventTypeValid &&
     areTermsAccepted;
    // console.log(validateDates(eventDetails.start_date, eventDetails.end_date), isEventNameValid, isEventDescriptionValid, isStartDateValid, isEndDateValid, isEventTypeValid,
    // isRegistrantLimitValid, areTermsAccepted)
    setIsValid(isFormValid);
    // console.log(isFormValid);
    onValidityChange(isFormValid);

  }, [eventDetails, eventType, TandC, onValidityChange]);

  const validateDates = (startDate: string | Date, endDate: string | Date): boolean => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    const isStartDateValid = start >= today;
    if (!startDate || !endDate) {
      return false;
    }
    const isEndDateValid = end >= start;
    const oneMonthDifference = 1000 * 60 * 60 * 24 * 30;
    const isWithinOneMonth = (end.getTime() - start.getTime()) <= oneMonthDifference;
    if (!isStartDateValid) {
      setDateError('Please select a date and time in the future for your event.');
    } else if (!isEndDateValid) {
      setDateError('Please select an end date and time after your start date and time')
    } else if (!isWithinOneMonth) {
      setDateError('Please select a date and time within 30 days of your start date.')
    } else {
      setDateError('');
    }
    return isStartDateValid && isEndDateValid && isWithinOneMonth;
  };

  return (
    <form style={{ marginBottom: '20px' }}>
      <h2>Event Details</h2>
      <h3>Hosted By {userRole?.host_name}</h3>

      <div className="form-group">
        <label htmlFor="event_name"><strong>Event Name</strong><span style={{ color: 'red' }}> *</span></label>
        <input
          type="text"
          id="event_name"
          name="event_name"
          value={eventDetails.event_name || ''}
          onChange={handleInputChange}
          placeholder="Enter event name"
          maxLength={255}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="event_description"><strong>Event Description</strong><span style={{ color: 'red' }}> *</span></label>
        <input
          id="event_description"
          name="event_description"
          value={eventDetails.event_description || ''}
          onChange={handleInputChange}
          placeholder="Enter event description"
          maxLength={500}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="location"><strong>Location</strong><span style={{ color: 'red' }}> *</span></label>
        <input
          type="text"
          id="location"
          name="location"
          value={eventDetails.location || ''}
          onChange={handleInputChange}
          maxLength={500}
          placeholder="123 Main Street, Virtual Invite Link, or Private"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="start_date"><strong>Start Date</strong><span style={{ color: 'red' }}> *</span></label>
        <input
          type="datetime-local"
          id="start_date"
          name="start_date"
          value={formatDateTime(eventDetails.start_date) || ''}
          onChange={handleInputChange}
          required
          min={minStartDate}
          max={maxStartDateStr}
          step={60}//5min
        />
      </div>
      <label style={{ color: 'red' }}>{dateError}</label>

      <div className="form-group">
        <label htmlFor="end_date"><strong>End Date</strong><span style={{ color: 'red' }}> *</span></label>
        <input
          type="datetime-local"
          id="end_date"
          name="end_date"
          value={formatDateTime(eventDetails.end_date) || ''}
          onChange={handleInputChange}
          required
          min={minEndDate}
          max={maxEndDateStr}
          step={60}//5min
        />
      </div>

      <div className="form-group">
        <label htmlFor="event_type_id"><strong>Event Type</strong><span style={{ color: 'red' }}> *</span></label>
        <select
          id="event_type_id"
          name="event_type_id"
          value={eventType}
          onChange={handleEventTypeChange}
        >
          <option value="">Select Event Type</option>
          {availableEventTypes.map(eventType => (
            <option key={eventType.event_type_id} value={eventType.event_type_id}>
              {eventType.event_type_name}
            </option>
          ))}
        </select>
      </div>

      {/* <div className="form-group">
        <label htlmlFor="organization_registrant_limit">
          <strong>Registration Limit Per User</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <input
          type="number"
          id="organization_registrant_limit"
          name="organization_registrant_limit"
          value={eventDetails.organization_registrant_limit || ''}
          onChange={handleInputChange}
          placeholder="Enter a whole number between 1 and 100,000"
          required
          min={1}
          max={100000}
          step={1}
        />
      </div> */}


      <div className="form-group">
        <label htmlFor="checkin_only"><strong>Use Quick Cyne Up?</strong><span style={{ color: 'red' }}> *</span></label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="checkin_only_yes"
              name="checkin_only"
              value="true"
              checked={eventDetails.checkin_only === true}
              onChange={handleInputChange}
            />
            Yes, this event will only keep track of registrants that Cyne Up at the event.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="checkin_only_no"
              name="checkin_only"
              value="false"
              checked={eventDetails.checkin_only === false}
              onChange={handleInputChange}
            />
            No, this event will have a registration and invitation process.
          </label>
        </div>
      </div>

      {!eventDetails.checkin_only && (<div className="form-group">
        <label htmlFor="has_invitation"><strong>Are you selecting Participants? (Invite Registrants To Event After They Cyne Up. Requires Account To Accept Invitation)</strong><span style={{ color: 'red' }}> *</span></label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="has_invitation_yes"
              name="has_invitation"
              value="true"
              checked={eventDetails.has_invitation === true}
              onChange={handleInputChange}
            />
            Yes, registrants will be selected after registering their information.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="has_invitation_no"
              name="has_invitation"
              value="false"
              checked={eventDetails.has_invitation === false}
              onChange={handleInputChange}
            />
            No, all registrants that Cyne Up may attend the event.
          </label>
        </div>
      </div>)}

      {!eventDetails.checkin_only && (<div className="form-group">
        <label htmlFor="has_checkin"><strong>Are Registrants Checking In at the Event?</strong><span style={{ color: 'red' }}> *</span></label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="has_checkin_yes"
              name="has_checkin"
              value="true"
              checked={eventDetails.has_checkin === true}
              onChange={handleInputChange}
            />
            Yes, registrants check in at the event.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="has_checkin_no"
              name="has_checkin"
              value="false"
              checked={eventDetails.has_checkin === false}
              onChange={handleInputChange}
            />
            No, registrants do not check in at the event.
          </label>
        </div>
      </div>)}

      <div className="form-group">
        <label htmlFor="has_judging">
          <input
            type="checkbox"
            id="has_judging"
            name="has_judging"
            checked={eventDetails.has_judging || false}
            onChange={handleInputChange}
          />
          <strong>Enable judging and rating capabilities for event</strong>
        </label>
      </div>

      {/* <div className="form-group">
        <label htmlFor="require_organization">
          <input
            type="checkbox"
            id="require_organization"
            name="require_organization"
            checked={eventDetails.require_organization || false}
            onChange={handleInputChange}
          />
          <strong>Require registrants to have an organization</strong>
        </label>
      </div> */}

      {/* <div className="form-group">
        <label htmlFor="handle_own_billing"><strong>Do you want to use our billing service?</strong><span style={{ color: 'red' }}> *</span></label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="use_billing_service_yes"
              name="use_billing_service"
              value="1"
              checked={eventDetails.use_billing_service === true}
              onChange={handleInputChange}
            />
            Yes, we plan to use the site's billing service for all billing-related items.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="use_billing_service_no"
              name="use_billing_service"
              value="0"
              checked={eventDetails.use_billing_service === false}
              onChange={handleInputChange}
            />
            No, we plan to use our own billing system.
          </label>
        </div>
      </div> */}

      {eventDetails.use_billing_service === true ? (
        <div className="form-group">
          <label htmlFor="registration_cost">
            <strong>Registration Cost (Does not include service charge)</strong>
            <span style={{ color: 'red' }}> *</span>
          </label>
          <input
            id="registration_cost"
            name="registration_cost"
            type="text"
            value={eventDetails.registrant_fee || ''}
            placeholder="$12.00"
            required
          />
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="expected_registrant_count">
            <strong>Expected Number of Registrants</strong>
            <span style={{ color: 'red' }}> *</span>
          </label>
          <input
            id="expected_registrant_count"
            name="expected_registrant_count"
            type="number"
            value={eventDetails.expected_registrant_count || ''}
            onChange={handleInputChange}
            placeholder="500"
            min={10}
            max={1000000}
            step={1}
            required
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="has_media">
          <input
            type="checkbox"
            id="has_media"
            name="has_media"
            checked={eventDetails.has_media || false}
            onChange={handleInputChange}
          />
          <strong>Allow Cyne Up Registration Form to attach media</strong>
        </label>
      </div>

      <div>
        <p><strong>Expected Cost of Event: </strong> ${ eventDetails.expected_cost }</p>
      </div>

      <div className="form-group">
        <label htmlFor="terms_and_conditions">
          <input
            type="checkbox"
            id="terms_and_conditions"
            name="terms_and_conditions"
            checked={TandC}
            onChange={() => setTandC(!TandC)}
          />
          <strong>I acknowledge that I have read and agree to be bound by the <a target="_blank" href="https://cyneup.com/terms-of-service">Terms of Service</a> and <a target="_blank" href="https://cyneup.com/privacy-policy">Privacy Policy</a>.</strong>
        </label>
      </div>
      <br />


      {/* <div className="form-group">
        <label htmlFor="use_invoice"><strong>Invoice</strong><span style={{ color: 'red' }}> *</span></label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="use_invoice_yes"
              name="use_invoice"
              value="1"
              checked={eventDetails.use_invoice === true}
              onChange={handleInputChange}
            />
            Send me an invoice.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="use_invoice_no"
              name="use_invoice"
              value="0"
              checked={eventDetails.use_invoice === false}
              onChange={handleInputChange}
            />
            Pay online when event is finished online.
          </label>
        </div>
      </div> */}
    </form>
  );
};
